import axios from 'axios'
import { stringify } from 'querystring'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useGetBaseRequestHeaders } from 'src/api/shared'
import { BrandConfig, EnvConfig } from 'src/configurations'
import { ExtendLocales } from 'src/enums'
import { ConfigResponse } from 'src/models/config.models'
import {
  ConfigState as AppConfigState,
  initialState as initalConfigState,
} from 'src/redux/reducers/data/config.reducer'

type ConfigContextActions = {
  /** Refreshes application configuration */
  refreshConfigContext: () => Promise<void>
  setLocale: (locale: ExtendLocales) => void
}

type ConfigContextState = {
  environment: EnvConfig
  brand: BrandConfig
  locale: ExtendLocales
  appConfig: AppConfigState
}

export type ConfigContextValue = ConfigContextActions & ConfigContextState

const stubConfigContextActions: ConfigContextActions = {
  refreshConfigContext: () => {
    throw Error('ConfigContext Not Available')
  },
  setLocale: () => {
    throw Error('ConfigContext Not Available')
  },
}

const defaultConfigContextState: ConfigContextState = {
  environment: JSON.parse(
    document.getElementById('__APP_CONFIG__')?.innerHTML || '{}',
  ),
  brand: JSON.parse(
    document.getElementById('__BRAND_CONFIG__')?.innerHTML || '{}',
  ),
  locale: window.navigator.language as ExtendLocales,
  appConfig: initalConfigState,
}

export const ConfigContext = createContext<ConfigContextValue>({
  ...defaultConfigContextState,
  ...stubConfigContextActions,
})

type ConfigContextProviderProps = {
  initialState: ConfigContextState
  children: ReactNode
}

export default function ConfigContextProvider(
  props: ConfigContextProviderProps,
): ReactElement {
  const { children, initialState } = props

  const [configContextState, setConfigContextState] =
    useState<ConfigContextState>(initialState)
  const [didLoad, setDidLoad] = useState<boolean>(false)
  const baseHeaders = useGetBaseRequestHeaders()
  const requestConfig = {
    ...baseHeaders,
    headers: {
      ...baseHeaders.headers,
      'Content-Type': 'multipart/form-data',
    },
    baseURL: baseHeaders.baseURL,
  }

  const refreshConfigContext = async (): Promise<void> => {
    const response = await axios.get<ConfigResponse>(
      `/config?${stringify({ locale: configContextState.locale })}`,
      requestConfig,
    )

    const config: AppConfigState = {
      ...response.data,
    }
    setConfigContextState({
      ...configContextState,
      appConfig: config,
    })
  }

  useEffect(() => {
    if (didLoad === false) {
      refreshConfigContext()
      setDidLoad(true)
    }
  }, [didLoad])

  // NOTE:
  // I am unsure whether locale should be stored in
  // ConfigProvider or UserProvider
  // I don't know enough about the application to make
  // an informed decision right now, so I'm going to
  // keep this here for now and come to a decision later
  const setLocale = (locale: ExtendLocales) => {
    setConfigContextState({
      ...configContextState,
      locale,
    })
  }

  return (
    <ConfigContext.Provider
      value={{
        ...configContextState,
        refreshConfigContext,
        setLocale,
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
